import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API } from '@app-core/constants/api.constants';
import { DEFAULT_DATA_RETENTION_DAYS, DEFAULT_DURATION_LIST, DEFAULT_MAX_ALLOWED_TAGS } from '@app-core/constants/constants';
import { ClientConfigResponse, Duration } from '@app-core/models/core.model';
import { getClientConfig } from '@config/client-config';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

// todo - refactor portal notification settings in a separate service for better logical separation
@Injectable({
  providedIn: 'root',
})
export class DurationRangeService {
  private durationList: Duration[] = DEFAULT_DURATION_LIST;

  private _dataRetentionDays: number = DEFAULT_DATA_RETENTION_DAYS;

  private _includePolylinePathInfo: boolean = false;

  private _webRTCLiveStreamingEnabled: boolean = false;
  private _disableSuprsendNotification: boolean = false;
  private _planDetails;
  private _plan;
  private _reportsLogoS3Url;

  private _supportAsyncAssetReport: boolean;
  private _isCoachingPageStandalone: boolean = false;
  private _selfCoachingEnabled: boolean = false;
  private _isSafetyEventsPageEnabled: boolean = false;

  private _autoRefreshEnabled: boolean = false;
  private _enableCustomReport: boolean = false;
  private _enableNewIncidentModal: boolean = false;
  private _enableConfigRedesign: boolean = false;
  private _enableGenerateInsights: boolean = false;
  private _enableVisualisation: boolean = false;
  private _supportAssetDiagnosticsTagFiltering: boolean = true;
  private _showDeviceHealthTab: boolean = false;
  private _showDeviceTrackingEventsTab: boolean = false;
  private _showGoDeviceLastStatus: boolean = false;
  private _maxAllowedTags: number = DEFAULT_MAX_ALLOWED_TAGS;
  private _showOnlyMyExportHistory: boolean = false;
  private _selectTodayAsDefaultRangeInTripsPage: boolean = false;
  private _isGeofenceEnabled: boolean = true;
  private _oldedVersionTagging: boolean = false;
  private _enableAudioCall: boolean = false;
  private _allowAudioCallForShield: boolean = false;
  private environment = getClientConfig().environments;

  public get dataRetentionDays() {
    return this._dataRetentionDays;
  }

  public get disableSuprsendNotification() {
    return this._disableSuprsendNotification;
  }

  public get planDetails() {
    return this._planDetails;
  }

  public get reportsLogoS3Url() {
    return this._reportsLogoS3Url;
  }
  public get plan() {
    return this._plan;
  }

  public get includePolylinePathInfo() {
    return this._includePolylinePathInfo;
  }

  public get supportAsyncAssetReport() {
    return this._supportAsyncAssetReport;
  }

  public get autoRefreshEnabled() {
    return this._autoRefreshEnabled;
  }

  public get isGeofenceEnabled() {
    return this._isGeofenceEnabled;
  }

  public get enableAudioCall() {
    return this._enableAudioCall;
  }

  public set dataRetentionDays(value: number) {
    this._dataRetentionDays = value;
    this.updateDurationList();
  }

  public set includePolylinePathInfo(value: boolean) {
    this._includePolylinePathInfo = value;
  }

  public get webRTCLiveStreamingEnabled() {
    return this._webRTCLiveStreamingEnabled;
  }

  public get selfCoachingEnabled() {
    return this._selfCoachingEnabled;
  }

  public get isCoachingPageStandalone() {
    return this._isCoachingPageStandalone;
  }

  public get enableCustomReport() {
    return this._enableCustomReport;
  }

  public get enableNewIncidentModal() {
    return this._enableNewIncidentModal;
  }

  public get isSafetyEventsPageEnabled() {
    return this._isSafetyEventsPageEnabled;
  }

  public get enableGenerateInsights() {
    return this._enableGenerateInsights;
  }

  public get enableVisualisation() {
    return this._enableVisualisation;
  }

  public set disableSuprsendNotification(value) {
    this._disableSuprsendNotification = value;
  }

  public get supportAssetDiagnosticsTagFiltering() {
    return this._supportAssetDiagnosticsTagFiltering;
  }

  public get showDeviceHealthTab() {
    return this._showDeviceHealthTab;
  }

  public get showDeviceTrackingEventsTab() {
    return this._showDeviceTrackingEventsTab;
  }

  public get showGoDeviceLastStatus() {
    return this._showGoDeviceLastStatus;
  }

  public get maxAllowedTags() {
    return this._maxAllowedTags;
  }

  public get enableConfigRedesign() {
    return this._enableConfigRedesign;
  }

  public get showOnlyMyExportHistory() {
    return this._showOnlyMyExportHistory;
  }

  public get selectTodayAsDefaultRangeInTripsPage() {
    return this._selectTodayAsDefaultRangeInTripsPage;
  }

  public get oldedVersionTagging() {
    return this._oldedVersionTagging;
  }

  constructor(private http: HttpClient) {}

  public getDurationList(): Duration[] {
    return this.durationList;
  }

  public updateDurationList() {
    this.durationList = this.getCustomDurationList(this.durationList, this.dataRetentionDays);
  }

  public getCustomDurationList(customDurationList: Duration[], dataRetentionPeriod = this.dataRetentionDays): Duration[] {
    let verifiedList = customDurationList.filter((item: Duration) => {
      if (item.days === 'custom' || item.days === 'yesterday' || item.days === 'today') {
        return true;
      } else if (typeof item.days === 'number') {
        return item.days <= dataRetentionPeriod;
      }
      return false;
    });
    return verifiedList;
  }

  public getClientConfig(): Observable<ClientConfigResponse> {
    return this.http.get<ClientConfigResponse>(API.CLIENT_CONFIG).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      tap((res: ClientConfigResponse) => {
        const {
          dataRetention = DEFAULT_DATA_RETENTION_DAYS,
          includePolylinePathInfo = false,
          disableSuprsend = false,
          webRTCLiveStreamingEnabled = false,
          planDetails,
          plan,
          enableConfigRedesign = false,
          supportAsyncAssetReport = false,
          reportsLogoS3Url,
          selfCoachingEnabled = false,
          autoRefreshEnabled = false,
          isCoachingPageStandalone = false,
          enableCustomReport = false,
          enableNewIncidentModal = false,
          enableGenerateInsights = false,
          enableVisualisation = false,
          showDeviceHealthTab = false,
          showDeviceTrackingEventsTab = false,
          showGoDeviceLastStatus = false,
          maxAllowedTags = DEFAULT_MAX_ALLOWED_TAGS,
          showOnlyMyExportHistory = false,
          selectTodayAsDefaultRangeInTripsPage = false,
          isSafetyEventsPageEnabled = false,
          geofenceEnabled = false,
          oldedVersionTagging = false,
          livestreaming,
        } = res?.data || {};
        const { enableAudioCall = false, allowAudioCallForShield = false } = livestreaming || {};
        this.dataRetentionDays = dataRetention;
        this._enableConfigRedesign = enableConfigRedesign;
        this.includePolylinePathInfo = includePolylinePathInfo;
        this._webRTCLiveStreamingEnabled = webRTCLiveStreamingEnabled;
        this._disableSuprsendNotification = disableSuprsend;
        this._planDetails = planDetails || null;
        this._plan = plan;
        this._supportAsyncAssetReport = supportAsyncAssetReport;
        this._reportsLogoS3Url = reportsLogoS3Url;
        this._selfCoachingEnabled = selfCoachingEnabled;
        this._autoRefreshEnabled = autoRefreshEnabled;
        this._isCoachingPageStandalone = isCoachingPageStandalone;
        this._enableCustomReport = enableCustomReport;
        this._enableNewIncidentModal = enableNewIncidentModal;
        this._enableGenerateInsights = enableGenerateInsights;
        this._enableVisualisation = enableVisualisation;
        this._showDeviceHealthTab = showDeviceHealthTab;
        this._showDeviceTrackingEventsTab = showDeviceTrackingEventsTab;
        this._showGoDeviceLastStatus = Boolean(this.environment?.isGeotabEnv) && showGoDeviceLastStatus;
        this._maxAllowedTags = maxAllowedTags;
        this._showOnlyMyExportHistory = showOnlyMyExportHistory;
        this._selectTodayAsDefaultRangeInTripsPage = selectTodayAsDefaultRangeInTripsPage;
        this._isSafetyEventsPageEnabled = isSafetyEventsPageEnabled;
        this._isGeofenceEnabled = geofenceEnabled && !this._planDetails;
        this._oldedVersionTagging = oldedVersionTagging;
        if (this._planDetails) {
          const featureFlags = this._planDetails.featureFlags || [];
          this._supportAssetDiagnosticsTagFiltering = featureFlags.includes('tagging_workflow');
        } else {
          this._supportAssetDiagnosticsTagFiltering = true;
        }
        this._enableAudioCall = enableAudioCall;
        this._allowAudioCallForShield = allowAudioCallForShield === true;
      }),
      catchError(() => {
        this.dataRetentionDays = DEFAULT_DATA_RETENTION_DAYS;
        this.includePolylinePathInfo = false;
        this._supportAsyncAssetReport = false;
        this._enableAudioCall = false;
        this._allowAudioCallForShield = false;
        return of(null);
      })
    );
  }

  private get isPushToTalkAllowedByPlan(): boolean {
    if (this._allowAudioCallForShield) {
      return true;
    }
    if (!this._planDetails) {
      return true;
    }
    const featureFlags = this._planDetails.featureFlags || [];
    return featureFlags.includes('push_to_talk');
  }

  private get isCompanionApp(): boolean {
    const companionAppToken = localStorage.getItem('companion-app-access-token');
    return Boolean(companionAppToken && companionAppToken.length > 0);
  }

  public shouldShowPTT(isAudioCallEnabledInPreferences: boolean): boolean {
    if (this.isCompanionApp) {
      return false;
    }
    const isAudioCallEnabledInClientConfig = Boolean(this._enableAudioCall);
    return Boolean(isAudioCallEnabledInPreferences) && isAudioCallEnabledInClientConfig && this.isPushToTalkAllowedByPlan;
  }
}
